"use client";

import React from "react";
import { motion } from "framer-motion";
import { colors, radius, spacing } from "@/ui/tokens";
import { useQuickReply } from "../../QuickReplyContext";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

export interface Suggestion {
  label: string;
  prompt: string;
}

interface SuggestFollowUpsOutput {
  suggestions: Suggestion[];
}

export function QuickRepliesDisplay({
  toolCall,
}: ToolDisplayProps<{ suggestions: Suggestion[] }, SuggestFollowUpsOutput>) {
  // Older conversations carry follow-ups as a suggestFollowUps tool call. It
  // had no execute, so its state stays "input-available" and the suggestions
  // are read from the input.
  if (toolCall.state !== "output-available" && toolCall.state !== "input-available") {
    return null;
  }

  return <QuickReplies suggestions={toolCall.finalOutput?.suggestions ?? toolCall.input?.suggestions ?? []} />;
}

/** Follow-up suggestion pills; clicking one sends its prompt as the recruiter's message. */
export function QuickReplies({ suggestions }: { suggestions: Suggestion[] }) {
  const quickReply = useQuickReply();

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: spacing[2],
        paddingTop: spacing[2],
      }}
    >
      {suggestions.map((suggestion, i) => (
        <motion.button
          key={suggestion.label}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, delay: i * 0.05 }}
          onClick={() => quickReply?.sendMessage(suggestion.prompt)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: `${spacing[1]}px ${spacing[3]}px`,
            borderRadius: radius.pill,
            border: `1px solid ${colors.border.subtle}`,
            backgroundColor: colors.canvas.default,
            color: colors.action.product,
            fontSize: 13,
            fontWeight: 500,
            lineHeight: "20px",
            cursor: "pointer",
            transition: "all 0.15s ease",
            whiteSpace: "nowrap",
          }}
          whileHover={{
            backgroundColor: colors.action.productLight,
            borderColor: colors.action.product,
          }}
          whileTap={{ scale: 0.97 }}
        >
          {suggestion.label}
        </motion.button>
      ))}
    </motion.div>
  );
}
